"use client";

import { useId, useMemo, useState } from "react";
import { useLocale } from "next-intl";

const AVERAGE_WORDS_PER_MINUTE = 200;

export default function WordCounter({ }: { toolName: string }) {
  const locale = useLocale();
  const idPrefix = useId();
  const [input, setInput] = useState("");
  const [copyLabel, setCopyLabel] = useState("Copy stats");

  const stats = useMemo(() => {
    const trimmed = input.replace(/\s+/g, " ").trim();
    const words = trimmed ? trimmed.split(/\s+/).length : 0;
    const chars = input.length;
    const charsNoSpace = input.replace(/\s+/g, "").length;

    // Sentences: count sequences ending in . ! or ?, ignoring an empty final chunk.
    const sentenceMatches = trimmed.match(/[^.!?]+[.!?]+|[^.!?]+$/g);
    const sentences = trimmed ? (sentenceMatches?.filter((s) => s.trim().length > 0).length ?? (trimmed ? 1 : 0)) : 0;

    // Paragraphs: blocks separated by one or more blank lines.
    const paragraphs = input.trim() ? input.trim().split(/\n\s*\n/).filter((p) => p.trim().length > 0).length : 0;

    const readingMinutes = words / AVERAGE_WORDS_PER_MINUTE;

    return { words, chars, charsNoSpace, sentences, paragraphs, readingMinutes };
  }, [input]);

  function formatReadingTime(minutes: number) {
    if (minutes <= 0) return "0 sec";
    if (minutes < 1) return `${Math.max(1, Math.round(minutes * 60))} sec`;
    const whole = Math.floor(minutes);
    const seconds = Math.round((minutes - whole) * 60);
    return seconds > 0 ? `${whole} min ${seconds} sec` : `${whole} min`;
  }

  async function copyStats() {
    const summary = [
      `Words: ${stats.words.toLocaleString(locale)}`,
      `Characters: ${stats.chars.toLocaleString(locale)}`,
      `Characters (no spaces): ${stats.charsNoSpace.toLocaleString(locale)}`,
      `Sentences: ${stats.sentences.toLocaleString(locale)}`,
      `Paragraphs: ${stats.paragraphs.toLocaleString(locale)}`,
      `Estimated reading time: ${formatReadingTime(stats.readingMinutes)}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(summary);
      setCopyLabel("Copied!");
    } catch {
      setCopyLabel("Copy failed");
    }
    window.setTimeout(() => setCopyLabel("Copy stats"), 1500);
  }

  function clearAll() {
    setInput("");
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor={`${idPrefix}-input`} className="text-sm font-medium text-slate-700">
            Your text
          </label>
          <button
            type="button"
            onClick={clearAll}
            disabled={!input}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Clear
          </button>
        </div>
        <textarea
          id={`${idPrefix}-input`}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          rows={10}
          placeholder="Type or paste your text here to see live word and character counts…"
          className="w-full rounded-xl border border-slate-300 p-4 text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-3">
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-2xl font-bold text-indigo-600">{stats.words.toLocaleString(locale)}</p>
          <p className="mt-1 text-xs text-slate-500">Words</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-2xl font-bold text-indigo-600">{stats.chars.toLocaleString(locale)}</p>
          <p className="mt-1 text-xs text-slate-500">Characters</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-2xl font-bold text-indigo-600">{stats.charsNoSpace.toLocaleString(locale)}</p>
          <p className="mt-1 text-xs text-slate-500">No spaces</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-2xl font-bold text-indigo-600">{stats.sentences.toLocaleString(locale)}</p>
          <p className="mt-1 text-xs text-slate-500">Sentences</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-2xl font-bold text-indigo-600">{stats.paragraphs.toLocaleString(locale)}</p>
          <p className="mt-1 text-xs text-slate-500">Paragraphs</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-2xl font-bold text-indigo-600">{formatReadingTime(stats.readingMinutes)}</p>
          <p className="mt-1 text-xs text-slate-500">Reading time</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => void copyStats()}
        disabled={!input}
        className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {copyLabel}
      </button>
    </div>
  );
}
